import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import BookDemoModal from "../components/BookDemoModal";
import DividerMotif from "../components/Brand/DividerMotif";
import Footer from "../components/Layout/Footer";
import { PublicHeader } from "../components/Layout/PublicHeader";
import { useDocumentMeta } from "../lib/useDocumentMeta";

/*
 * Velaigal landing.
 *
 * The distinctive move: Tamil typography is the visual subject of the
 * hero, not a watermark. The word வேலைகள் fills the hero zone at clamp
 * font sizes (7rem-18rem) in ink at ~8% opacity, behind the Latin
 * headline. On first paint, the hero plays a single 1.8s orchestrated
 * reveal — Tamil glyph climbs in (1400ms ease-out-expo), label, headline
 * lines, the saffron rule draw, subhead, CTAs, and the editorial
 * footnote about the Tamil meaning all enter on a deliberate timeline.
 *
 * Section transitions use <DividerMotif/> — a small ink+saffron ornament
 * derived from the Tamil consonant signature, used like a fleuron in
 * old typography.
 */

const VOICES = [
  {
    quote:
      "We stopped reading resumes and started reviewing work. What reaches our shortlist now is people who already proved they can do the job.",
    attribution: "A HIRING LEAD AT A FINTECH STARTUP",
  },
  {
    quote:
      "Applying meant building something real, not answering multiple-choice questions. I'd rather spend an evening on an actual task than guess what a quiz wants.",
    attribution: "A BACKEND ENGINEER",
  },
  {
    quote:
      "The pre-screen catches the submissions that don't even load before we spend an hour on them. We still make every call ourselves.",
    attribution: "AN ENGINEERING MANAGER",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Post a job",
    body: "Write the role and company context. Velaigal drafts two or three real-world tasks candidates can build in a few hours to a weekend — for technical and non-technical roles alike.",
  },
  {
    number: "02",
    title: "Candidates build and submit",
    body: "Applying means doing the work: a hosted link, a GitHub repo, a document or a video. No submission, no application.",
  },
  {
    number: "03",
    title: "AI pre-screens, you decide",
    body: "Every submission is checked against the role's rubric, scored and flagged. It never auto-rejects — you review the evidence and shortlist.",
  },
];

const LandingPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useDocumentMeta(
    "Velaigal — Hiring built on what people can actually do.",
    "Post a job and Velaigal's AI drafts real-world tasks from it. Candidates build and submit actual work, AI pre-screens every submission, and you decide who to shortlist. Free during early access, India-first."
  );

  return (
    <div className="min-h-screen w-full bg-cream text-ink">
      <PublicHeader />

      {/* ---------------- Hero ---------------- *
       * Tamil typography is the subject. The word வேலைகள் fills the zone
       * in ink at low opacity, the Latin headline composes in front.
       */}
      <section className="relative min-h-[calc(100vh-72px)] flex items-center overflow-hidden">
        {/* The distinctive move: Tamil hero glyph as primary visual */}
        <div className="velaigal-tamil-hero" aria-hidden="true">
          <span>வேலைகள்</span>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-8 py-12 w-full">
          <span
            className="velaigal-reveal label-editorial block"
            style={{ animationDelay: "250ms" }}
          >
            Hiring · India · 2026
          </span>

          <h1
            className="font-display text-5xl md:text-6xl lg:text-[80px] font-medium text-ink leading-[1.04] tracking-tight mt-6 max-w-4xl"
            style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
          >
            <span
              className="velaigal-reveal block"
              style={{ animationDelay: "350ms" }}
            >
              Hiring built on what
            </span>
            <span
              className="velaigal-reveal block"
              style={{ animationDelay: "480ms" }}
            >
              people can <span className="text-saffron">actually do.</span>
            </span>
          </h1>

          <span
            aria-hidden="true"
            className="velaigal-rule-draw block w-16 h-px bg-saffron mt-8"
          />

          <p
            className="velaigal-reveal mt-8 text-lg lg:text-xl text-ink-secondary max-w-xl leading-relaxed"
            style={{ animationDelay: "900ms" }}
          >
            Post a job and Velaigal drafts real-world tasks from it. Candidates
            apply by building and submitting actual work, not a polished resume.
          </p>

          <div
            className="velaigal-reveal mt-8 flex flex-wrap items-center gap-5"
            style={{ animationDelay: "1100ms" }}
          >
            <Link
              to="/signup/hr"
              className="btn-primary no-underline velaigal-link-arrow"
              style={{ textDecoration: "none" }}
            >
              Start hiring
              <ArrowRight className="velaigal-link-arrow__icon h-4 w-4 ml-1" strokeWidth={1.75} />
            </Link>
            <Link
              to="/signup/jobseeker"
              className="velaigal-link text-sm font-medium inline-flex items-center velaigal-link-arrow"
              style={{ textDecoration: "none" }}
            >
              I&apos;m looking for work
              <ArrowRight className="velaigal-link-arrow__icon h-4 w-4 ml-1.5" strokeWidth={1.75} />
            </Link>
          </div>

          {/* Editorial annotation about the Tamil word — the brand
           * footnote that makes the hero typography legible to
           * non-Tamil readers without explaining itself loudly */}
          <div
            className="velaigal-reveal mt-12 pt-6 border-t border-hairline flex flex-wrap items-baseline gap-4"
            style={{ animationDelay: "1400ms" }}
          >
            <span
              style={{
                fontFamily: '"Noto Serif Tamil", Georgia, serif',
                fontSize: "24px",
                fontWeight: 500,
                color: "var(--color-ink)",
                lineHeight: 1,
              }}
            >
              வேலைகள்
            </span>
            <span className="label-editorial">
              "Velaigal" · Tamil for "works"
            </span>
            <span className="text-sm text-ink-tertiary">
              Made for hiring teams and jobseekers across the World.
            </span>
          </div>
        </div>
      </section>

      {/* ---------------- The product flow (cards) ---------------- *
       * Pulled out of the hero column so the hero owns its one moment.
       * These cards now read as their own beat below the fold.
       */}
      <section className="border-t border-hairline">
        <div className="max-w-7xl mx-auto px-8 py-20">
          <span className="label-editorial">From job post to shortlist</span>
          <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {[
              {
                step: "Step 1",
                title: "AI drafts the tasks",
                body: "From your JD, Velaigal writes two or three real-world tasks in minutes. You edit, write your own, or mix both.",
              },
              {
                step: "Step 2",
                title: "Candidates submit real work",
                body: "A hosted link, a GitHub repo, a document, or a video — not a quiz, and the same tasks for every applicant.",
              },
              {
                step: "Step 3",
                title: "AI pre-screens, you decide",
                body: "Every submission checked and scored against your rubric. You review the evidence and shortlist.",
              },
            ].map((card) => (
              <article
                key={card.step}
                className="bg-paper border border-hairline rounded-md p-6 transition-colors hover:border-hairline-strong"
              >
                <span className="label-editorial">{card.step}</span>
                <h3
                  className="font-display text-xl text-ink mt-3 leading-snug"
                  style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
                >
                  {card.title}
                </h3>
                <p className="mt-3 text-sm text-ink-tertiary leading-relaxed">
                  {card.body}
                </p>
              </article>
            ))}
          </div>
        </div>

        <DividerMotif className="py-2" />
      </section>

      {/* ---------------- How it works ---------------- */}
      <section className="max-w-7xl mx-auto px-8 py-24">
        <span className="label-editorial">How it works</span>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-hairline">
          {STEPS.map((step) => (
            <div key={step.number} className="bg-cream p-12 md:p-16">
              <span
                className="font-display text-7xl font-medium block leading-none mb-8"
                style={{
                  WebkitTextStroke: "1px var(--color-ink)",
                  WebkitTextFillColor: "transparent",
                  fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0',
                }}
              >
                {step.number}
              </span>
              <h3
                className="font-display text-2xl font-medium text-ink leading-snug velaigal-headline-hover"
                style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
              >
                {step.title}
              </h3>
              <p className="mt-4 text-sm text-ink-secondary leading-relaxed">
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Voices ---------------- */}
      <section>
        <div className="max-w-5xl mx-auto px-8 py-24">
          <span className="label-editorial">Voices</span>
          <div className="mt-12 space-y-16">
            {VOICES.map((voice, idx) => (
              <figure
                key={idx}
                className={idx > 0 ? "pt-16 border-t border-hairline" : ""}
              >
                <blockquote
                  className="font-display italic text-2xl md:text-3xl font-normal text-ink leading-snug"
                  style={{ fontVariationSettings: '"opsz" 72, "SOFT" 50, "WONK" 0' }}
                >
                  "{voice.quote}"
                </blockquote>
                <figcaption className="mt-6 label-editorial">
                  {voice.attribution}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <DividerMotif className="py-2" />

      {/* ---------------- Two doors ---------------- */}
      <section>
        <div className="max-w-7xl mx-auto px-8 py-24 grid grid-cols-1 md:grid-cols-2 gap-px bg-hairline">
          <div className="bg-cream p-12 md:p-16">
            <span className="label-editorial">For recruiters</span>
            <h2
              className="font-display text-3xl md:text-4xl font-medium text-ink leading-tight mt-6 velaigal-headline-hover"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              I&apos;m hiring.
            </h2>
            <p className="mt-6 text-base text-ink-secondary max-w-md leading-relaxed">
              Write the role and Velaigal drafts real-world tasks from it.
              Every submission is pre-screened and scored automatically.
              Review the evidence and shortlist with confidence.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/signup/hr"
                className="btn-primary no-underline"
                style={{ textDecoration: "none" }}
              >
                Start hiring
              </Link>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className="btn-secondary"
              >
                Book a demo
              </button>
            </div>
          </div>

          <div className="bg-cream p-12 md:p-16">
            <span className="label-editorial">For job seekers</span>
            <h2
              className="font-display text-3xl md:text-4xl font-medium text-ink leading-tight mt-6 velaigal-headline-hover"
              style={{ fontVariationSettings: '"opsz" 144, "SOFT" 50, "WONK" 0' }}
            >
              I&apos;m looking for work.
            </h2>
            <p className="mt-6 text-base text-ink-secondary max-w-md leading-relaxed">
              Prove your skills by building, not by quizzing. Browse open
              roles, pick the ones that fit, and apply by submitting real work.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/jobs"
                className="btn-primary no-underline"
                style={{ textDecoration: "none" }}
              >
                Browse open jobs
              </Link>
              <Link
                to="/signup/jobseeker"
                className="btn-secondary no-underline"
                style={{ textDecoration: "none" }}
              >
                Create your profile
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- Footer ---------------- */}
      <Footer />

      <BookDemoModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default LandingPage;
